﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcGridTransposed from '@mescius/wijmo.grid.transposed';
var WjTransposedGridBehavior = /** @class */ (function (_super) {
    __extends(WjTransposedGridBehavior, _super);
    function WjTransposedGridBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjTransposedGridBehavior.tag = 'wj-transposed-grid';
    WjTransposedGridBehavior.props = [
        'autoGenerateRows',
        'isDisabled',
        'tabOrder',
        'newRowAtTop',
        'allowAddNew',
        'allowDelete',
        'allowDragging',
        'allowMerging',
        'allowResizing',
        'allowSorting',
        'allowPinning',
        'autoScroll',
        'autoRowHeights',
        'autoSizeMode',
        'autoGenerateColumns',
        'autoSearch',
        'caseSensitiveSearch',
        'quickAutoSize',
        'bigCheckboxes',
        'childItemsPath',
        'groupHeaderFormat',
        'headersVisibility',
        'showSelectedHeaders',
        'showMarquee',
        'showPlaceholders',
        'itemFormatter',
        'isReadOnly',
        'imeEnabled',
        'mergeManager',
        'selectionMode',
        'showGroups',
        'showSort',
        'showDropDown',
        'showAlternatingRows',
        'showErrors',
        'alternatingRowStep',
        'itemValidator',
        'validateEdits',
        'treeIndent',
        'itemsSource',
        'autoClipboard',
        'expandSelectionOnCopyPaste',
        'frozenRows',
        'frozenColumns',
        'cloneFrozenCells',
        'deferResizing',
        'sortRowIndex',
        'editColumnIndex',
        'stickyHeaders',
        'preserveSelectedState',
        'preserveOutlineState',
        'preserveWhiteSpace',
        'keyActionTab',
        'keyActionEnter',
        'rowHeaderPath',
        'virtualizationThreshold',
        'anchorCursor',
        'lazyRender',
        'refreshOnEdit',
        'copyHeaders',
        'columnGroups',
        'rowGroups',
        'commitEmptyEdits',
        'headersFocusability',
    ];
    WjTransposedGridBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'beginningEdit',
        'cellEditEnded',
        'cellEditEnding',
        'prepareCellForEdit',
        'formatItem',
        'resizingColumn',
        'resizedColumn',
        'autoSizingColumn',
        'autoSizedColumn',
        'draggingColumn',
        'draggingColumnOver',
        'draggedColumn',
        'sortingColumn',
        'sortedColumn',
        'pinningColumn',
        'pinnedColumn',
        'resizingRow',
        'resizedRow',
        'autoSizingRow',
        'autoSizedRow',
        'draggingRow',
        'draggingRowOver',
        'draggedRow',
        'deletingRow',
        'deletedRow',
        'loadingRows',
        'loadedRows',
        'rowEditStarting',
        'rowEditStarted',
        'rowEditEnding',
        'rowEditEnded',
        'rowAdded',
        'groupCollapsedChanging',
        'groupCollapsedChanged',
        'columnGroupCollapsedChanging',
        'columnGroupCollapsedChanged',
        'itemsSourceChanging',
        'itemsSourceChanged',
        'selectionChanging',
        'selectionChanged',
        'scrollPositionChanged',
        'updatingView',
        'updatedView',
        'updatingLayout',
        'updatedLayout',
        'pasting',
        'pasted',
        'pastingCell',
        'pastedCell',
        'copying',
        'copied'
    ];
    WjTransposedGridBehavior.classCtor = function () { return wjcGridTransposed.TransposedGrid; };
    return WjTransposedGridBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.grid.transposed.TransposedGrid} control.
 *
 * The <b>wj-transposed-grid</b> component may contain
 * a {@link wijmo.vue2.grid.transposed.WjTransposedGridRow} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.grid.transposed.TransposedGrid} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjTransposedGrid = WjTransposedGridBehavior.register();
function registerV3WjTransposedGrid(app) {
    app.component(WjTransposedGridBehavior.tag, WjTransposedGrid);
}
var WjTransposedGridRowBehavior = /** @class */ (function (_super) {
    __extends(WjTransposedGridRowBehavior, _super);
    function WjTransposedGridRowBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjTransposedGridRowBehavior.prototype._initParent = function () {
        var grid = this.parent.control;
        if (grid.autoGenerateRows) {
            grid.autoGenerateRows = false;
            grid._rowInfo.clear();
        }
        _super.prototype._initParent.call(this);
    };
    WjTransposedGridRowBehavior.tag = 'wj-transposed-grid-row';
    WjTransposedGridRowBehavior.parentProp = '_rowInfo';
    WjTransposedGridRowBehavior.props = [
        'wjProperty',
        'name',
        'dataMap',
        'dataType',
        'binding',
        'sortMemberPath',
        'format',
        'cellTemplate',
        'header',
        'width',
        'maxLength',
        'minWidth',
        'maxWidth',
        'align',
        'allowDragging',
        'allowSorting',
        'allowResizing',
        'allowMerging',
        'aggregate',
        'isReadOnly',
        'cssClass',
        'cssClassAll',
        'isContentHtml',
        'isSelected',
        'visible',
        'wordWrap',
        'multiLine',
        'mask',
        'inputType',
        'isRequired',
        'showDropDown',
        'dataMapEditor',
        'dropDownCssClass',
        'quickAutoSize',
        'editor',
        'placeholder'
    ];
    WjTransposedGridRowBehavior.events = [
        'initialized'
    ];
    WjTransposedGridRowBehavior.changeEvents = {
        'grid.selectionChanged': ['isSelected'],
    };
    WjTransposedGridRowBehavior.classCtor = function () { return wjcGridTransposed.TransposedGridRow; };
    return WjTransposedGridRowBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.grid.transposed.TransposedGridRow} class.
 *
 * The <b>wj-transposed-grid-row</b> component should be contained in
 * a {@link wijmo.vue2.grid.transposed.WjTransposedGrid} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.grid.transposed.TransposedGridRow} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjTransposedGridRow = WjTransposedGridRowBehavior.register();
function registerV3WjTransposedGridRow(app) {
    app.component(WjTransposedGridRowBehavior.tag, WjTransposedGridRow);
}
export function registerGridTransposed(app) {
    if (VueApi.isV3Plus) {
        registerV3WjTransposedGrid(app);
        registerV3WjTransposedGridRow(app);
    }
}
